import { entityDefinition } from '@work4all/models/lib/Classes/entityDefinitions';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { areEntitiesInSameGroup, GroupEntities } from '@work4all/utils';

import { UseEntityRecordMatcherProps } from '../types';

import { TargetEntityRecordMatcherConfigs } from './types';

type Props<TData> = Pick<
  UseEntityRecordMatcherProps,
  'sourceEntity' | 'targetEntity'
> & {
  config: TargetEntityRecordMatcherConfigs<TData>;
};

const GROUP_RESTRICTED_ENTITIES = [Entities.topicSimple];

export const createSourceEntityRecordMatcherRequestData = <TData>({
  sourceEntity,
  targetEntity,
  config,
}: Props<TData>): TData => {
  const fieldsMapping =
    config?.fieldsMappingBySourceEntity?.[sourceEntity] ?? {};

  const restrictedFieldsToSourceEntities =
    config?.restrictedFieldsToSourceEntities ?? {};

  return Object.entries(config.fields).reduce(
    (acc, [targetEntityField, value]) => {
      const restrictedFieldToSourceEntities =
        restrictedFieldsToSourceEntities[targetEntityField];

      if (
        restrictedFieldToSourceEntities &&
        !restrictedFieldToSourceEntities.includes(sourceEntity)
      ) {
        return acc;
      }

      const targetEntityFieldDefinition =
        entityDefinition[targetEntity].fieldDefinitions;

      const targetEntityFieldDefinitionEntity =
        targetEntityFieldDefinition[targetEntityField].entity;

      // Workaround gonna be changed in WW-7211
      if (
        targetEntityField === 'businessPartner' &&
        (sourceEntity === Entities.customer ||
          sourceEntity === Entities.supplier)
      ) {
        return { ...acc, ...value['data'][sourceEntity] };
      } else if (targetEntityFieldDefinitionEntity === sourceEntity) {
        return { ...acc, ...value };
      }

      const sourceEntityField =
        fieldsMapping[targetEntityField] ?? targetEntityField;

      if (
        isValidField(
          sourceEntity,
          targetEntity,
          sourceEntityField,
          targetEntityField
        )
      ) {
        acc[sourceEntityField] = value;
      }

      return acc;
    },
    {}
  ) as TData;
};

const isValidField = (
  sourceEntity: Entities,
  targetEntity: Entities,
  sourceEntityField: string,
  targetEntityField: string
) => {
  const sourceEntityFieldDefinition =
    entityDefinition[sourceEntity].fieldDefinitions;
  const targetEntityFieldDefinition =
    entityDefinition[targetEntity].fieldDefinitions;

  if (!sourceEntityFieldDefinition || !targetEntityFieldDefinition) {
    return false;
  }

  const isFieldExistsInSourceEntity = Object.prototype.hasOwnProperty.call(
    sourceEntityFieldDefinition,
    sourceEntityField
  );

  const isTargetEntityHasField = Object.prototype.hasOwnProperty.call(
    targetEntityFieldDefinition,
    targetEntityField
  );

  if (!isFieldExistsInSourceEntity || !isTargetEntityHasField) {
    return false;
  }

  const sourceEntityFieldDefinitionEntity =
    sourceEntityFieldDefinition[sourceEntityField].entity;

  const targetEntityFieldDefinitionEntity =
    targetEntityFieldDefinition[targetEntityField].entity;

  if (sourceEntityFieldDefinitionEntity !== targetEntityFieldDefinitionEntity) {
    return false;
  }

  const isGroupRestrictedEntity = GROUP_RESTRICTED_ENTITIES.includes(
    sourceEntityFieldDefinitionEntity
  );

  if (isGroupRestrictedEntity) {
    return areEntitiesInSameGroup(
      sourceEntity as GroupEntities,
      targetEntity as GroupEntities
    );
  }

  return true;
};
